import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import {
  X,
  Save,
  RefreshCw,
  ShieldCheck,
  Hash,
  Layers,
  Building,
  User,
  Tag,
  MessageSquare,
  Check,
  CheckCircle2,
  AlertCircle,
  GitBranch,
  History,
  ChevronDown,
  Search,
} from "lucide-react";

import { useMsal } from "@azure/msal-react";
import { callApi } from "../../api/api";
import RequestHistoryModal from "../../components/RequestHistoryModal";

const POLICY_NUMBER_CHAR_PATTERN = /^[A-Za-z0-9\s]*$/;

function sanitizePolicyNumberInput(value) {
  return String(value || "").replace(/[^A-Za-z0-9\s]/g, "");
}

function hasValidPolicyNumberChars(value) {
  return POLICY_NUMBER_CHAR_PATTERN.test(String(value || "").trim());
}

function getLobDisplayLabel(lobCode, lobs) {
  const code = String(lobCode || "").trim();
  if (!code) return "";

  const match = lobs.find((item) => item.lookup_code === code);
  return match?.lookup_name || code;
}

export default function AssignmentMetadataPanel({
  row,
  activeTab,
  onClose,
  onSaved,
  onPreview,
  previewLoading,
  onReindex,
  isWorkPool = false,
  source = null,
}) {
  const { instance, accounts } = useMsal();

  const [form, setForm] = useState({
    document_type: "",
    policy_number: "",
    lob: "",
    agency_no: "",
    primary_insured: "",
    review_comments: "",
    department: "",
  });

  const [documentTypes, setDocumentTypes] = useState([]);
  const [lobs, setLobs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roleMappings, setRoleMappings] = useState([]);
  const [movements, setMovements] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [saving, setSaving] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [closeRequest, setCloseRequest] = useState(false);
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);

  const [error, setError] = useState("");
  const [errorModal, setErrorModal] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [originalDocumentType, setOriginalDocumentType] = useState("");
  const [originalDepartment, setOriginalDepartment] = useState("");

  /* =========================================
     LOAD LOOKUPS
  ========================================= */

  const getDepartmentForDocType = useCallback((docType, currentDept, mappingsList) => {
    if (!docType) return "N/A";
    const cleanDoc = docType.toUpperCase().replace(/\s+/g, "_");
    const list = mappingsList || roleMappings;
    const match = list.find((m) => {
      const mDoc = (m.documentType || m.document_type || "").toUpperCase().replace(/\s+/g, "_");
      const mName = (m.documentTypeName || "").toUpperCase().replace(/\s+/g, "_");
      return mDoc === cleanDoc || mName === cleanDoc;
    });
    if (match) {
      return match.roleName || match.roleCode || match.role_code || "N/A";
    }
    return "N/A";
  }, [roleMappings]);

  const loadLookups = useCallback(async () => {
    try {
      const [documentTypeRes, lobRes, deptRes, mappingRes] = await Promise.all([
        callApi(instance, accounts, "GET", "/api/admin/lookup/document_type"),
        callApi(instance, accounts, "GET", "/api/admin/lookup/line_of_business"),
        callApi(instance, accounts, "GET", "/api/departments").catch(() => null),
        callApi(instance, accounts, "GET", "/api/assignments/role-mappings").catch(() => null),
      ]);
      setDocumentTypes(documentTypeRes?.data?.data || []);
      setLobs(lobRes?.data?.data || []);
      setDepartments(
        Array.isArray(deptRes?.data?.data)
          ? deptRes.data.data
          : Array.isArray(deptRes?.data)
            ? deptRes.data
            : [],
      );
      setRoleMappings(
        Array.isArray(mappingRes?.data?.data)
          ? mappingRes.data.data
          : Array.isArray(mappingRes?.data)
            ? mappingRes.data
            : []
      );
    } catch (err) {
      console.error(err);
    }
  }, [instance, accounts]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  /* =========================================
     UPDATE FORM WHEN ROW CHANGES
  ========================================= */

  useEffect(() => {
    if (row) {
      const docType = row.document_type || "";
      const dept = row.current_department || row.currentDepartment || row.department || row.department_name || "N/A";

      setForm({
        document_type: docType,
        policy_number: row.policy_number || "",
        lob: row.lob || "",
        agency_no: row.agency_no || "",
        primary_insured: row.primary_insured || "",
        review_comments: row.review_comments || "",
        department: dept,
      });
      setOriginalDocumentType(docType);
      setOriginalDepartment(dept);

      setUpdated(false);
      setError("");
      setSuccessMessage("");

      if (row.msg_id) {
        callApi(instance, accounts, "GET", `/api/departments/movements?msgId=${row.msg_id}`)
          .then((res) => {
            const list = Array.isArray(res?.data?.data)
              ? res.data.data
              : Array.isArray(res?.data)
                ? res.data
                : [];
            setMovements(list);
          })
          .catch(() => setMovements([]));
      }
    }
  }, [row, instance, accounts, getDepartmentForDocType, roleMappings]);

  /* =========================================
     UPDATE FIELD & DERIVED STATES
  ========================================= */

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDocumentTypeChange = (newDocType) => {
    setForm((prev) => ({
      ...prev,
      document_type: newDocType,
    }));
  };

  const documentTypeChanged = form.document_type !== originalDocumentType;
  const formDisabled = saving || updated;
  const hasLob = Boolean(row?.lob?.trim());

  const lobOptions = useMemo(() => {
    return lobs.map((item) => ({
      value: item.lookup_code,
      label: item.lookup_name || item.lookup_code,
    }));
  }, [lobs]);

  const docTypeOptions = useMemo(() => {
    return documentTypes.map((item) => ({
      value: item.lookup_code,
      label: item.lookup_name || item.lookup_code,
    }));
  }, [documentTypes]);

  /* =========================================
     VALIDATION & SAVE
  ========================================= */

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const documentTypeChanged =
        form.document_type !== originalDocumentType;

      if (!form.document_type) {
        setError("Document Type is required");
        return;
      }

      if (!form.policy_number?.trim()) {
        setError("Policy Number is required");
        return;
      }

      if (!hasValidPolicyNumberChars(form.policy_number)) {
        setError(
          "Policy Number can only contain letters, numbers, and spaces",
        );
        return;
      }

      if (documentTypeChanged && !form.review_comments?.trim()) {
        setError("Review Comments are required when Document Type is changed");
        return;
      }

      const res = await callApi(
        instance,
        accounts,
        "PUT",
        `/api/assignments/update-metadata/${row.msg_id}`,
        {
          ...form,
          policy_number: form.policy_number.trim(),
          department: form.department,
          verified_by: accounts?.[0]?.name || accounts?.[0]?.username || "User",
          source: "VIEW_ASSIGNMENT",
          close_request: closeRequest,
          ado_user_story_id: row.ado_user_story_id || row.id || null,
        },
      );

      setUpdated(true);
      setSuccessMessage(
        res?.data?.message || (closeRequest ? "Request closed & verified successfully!" : "Metadata updated successfully!")
      );
      setOriginalDocumentType(form.document_type);
      setOriginalDepartment(form.department);

      // Immediately reload stage verification history from database
      if (row.msg_id) {
        try {
          const moveRes = await callApi(instance, accounts, "GET", `/api/departments/movements?msgId=${row.msg_id}`);
          const list = Array.isArray(moveRes?.data?.data) ? moveRes.data.data : Array.isArray(moveRes?.data) ? moveRes.data : [];
          setMovements(list);
        } catch {
          // ignore
        }
      }

      const apiData = res?.data?.data || res?.data || {};
      const updatedAssignee = apiData.assigned_to || apiData.assignedTo || null;

      // Notify parent with the full updated metadata for real-time re-rendering
      onSaved?.({
        ...form,
        msg_id: row.msg_id,
        policy_number: form.policy_number.trim(),
        document_type: form.document_type,
        department: form.department,
        current_department: form.department,
        assigned_to: updatedAssignee,
        assignedTo: updatedAssignee,
        verified_flag: 1,
        verified_by: accounts?.[0]?.name || accounts?.[0]?.username || "User",
        is_closed: closeRequest,
        state: closeRequest ? "Closed" : undefined,
      });

      if (closeRequest) {
        setTimeout(() => {
          onClose?.();
        }, 1200);
      }
    } catch (err) {
      console.error("Failed to update metadata:", err);
      const errMsg = err?.response?.data?.message || err.message || "Failed to update metadata. Please check the fields and try again.";
      setErrorModal(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDismissErrorModal = () => {
    setErrorModal(null);
    setError("");

    // Re-render & reset form state back to original database values
    const origDoc = originalDocumentType || row.document_type || "";
    const origDept = originalDepartment || row.department || row.current_department || "N/A";
    setForm((prev) => ({
      ...prev,
      document_type: origDoc,
      department: origDept,
      policy_number: row.policy_number || "",
      lob: row.lob || "",
      agency_no: row.agency_no || "",
      primary_insured: row.primary_insured || "",
      review_comments: row.review_comments || "",
    }));

    // Re-render parent thread view with original un-updated values
    onSaved?.({
      ...row,
      msg_id: row.msg_id,
      document_type: origDoc,
      department: origDept,
      current_department: origDept,
    });
  };

  const update = handleSave;

  if (!row) {
    return <div style={styles.empty}>Select a record to review</div>;
  }

  return (
    <div style={styles.container}>
      {/* =========================================
          HEADER
      ========================================= */}

      <div style={styles.header}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ShieldCheck size={16} color="#2563eb" />
            <h3 style={styles.title}>Review &amp; Classification</h3>
          </div>
          <div style={styles.sub}>Update claim metadata</div>
        </div>

        <button style={styles.closeBtn} onClick={onClose} title="Close review">
          <X size={18} />
        </button>
      </div>

      {/* =========================================
          EMAIL SUMMARY
      ========================================= */}

      {/* <div style={styles.emailCard}>
        <div style={styles.emailRow}>
          <Mail size={16} />

          <div>
            <div style={styles.emailName}>
              {row.mail_from_name || row.mail_from_address || "Unknown Sender"}
            </div>

            <div style={styles.emailAddress}>{row.mail_from_address}</div>
          </div>
        </div>

        <div style={styles.subject}>{row.email_subject || "No Subject"}</div>

        <div style={styles.attachments}>
          {row.attachments?.length || 0} attachment(s)
        </div>
      </div> */}

      {/* =========================================
          FORM & NOTIFICATIONS
      ========================================= */}

      <div style={styles.scrollableContent} className="scrollable">
        {/* Success Alert Banner */}
        {successMessage && (
          <div
            style={{
              margin: "12px 16px 0 16px",
              padding: "10px 12px",
              borderRadius: "8px",
              background: "#ecfdf5",
              border: "1px solid #a7f3d0",
              color: "#065f46",
              fontSize: "12.5px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              animation: "fadeIn 0.2s ease-in-out"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CheckCircle2 size={16} color="#059669" style={{ flexShrink: 0 }} />
              <span>{successMessage}</span>
            </div>
            <button
              onClick={() => setSuccessMessage("")}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                color: "#059669",
                display: "flex"
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Error Alert Banner */}
        {error && (
          <div
            style={{
              margin: "12px 16px 0 16px",
              padding: "10px 12px",
              borderRadius: "8px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: "12.5px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              animation: "fadeIn 0.2s ease-in-out"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError("")}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                color: "#dc2626",
                display: "flex"
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div style={styles.formSection}>
          <Field
            label="Policy Number"
            icon={Hash}
            disabled={formDisabled}
            value={form.policy_number}
            onChange={(v) =>
              updateField("policy_number", sanitizePolicyNumberInput(v))
            }
          />

          {hasLob ? (
            <Field
              label="LOB"
              icon={Layers}
              disabled
              value={getLobDisplayLabel(row.lob, lobs)}
              onChange={() => { }}
            />
          ) : (
            <div>
              <label style={styles.label}>
                <Layers size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-1px" }} />
                LOB
              </label>

              <SearchableSelect
                options={lobOptions}
                value={form.lob}
                onChange={(val) => updateField("lob", val)}
                placeholder="Select LOB"
                disabled={formDisabled}
                allowClear
                showSearch={lobOptions.length > 5}
              />
            </div>
          )}

          <Field
            label="Agency No"
            icon={Building}
            disabled={formDisabled}
            value={form.agency_no}
            onChange={(v) => updateField("agency_no", v)}
          />

          <Field
            label="Primary Insured"
            icon={User}
            disabled={formDisabled}
            value={form.primary_insured}
            onChange={(v) => updateField("primary_insured", v)}
          />

          <div>
            <label style={styles.label}>
              <Tag size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-1px" }} />
              Document Type
            </label>

            <SearchableSelect
              options={docTypeOptions}
              value={form.document_type}
              onChange={handleDocumentTypeChange}
              placeholder="Select Document Type"
              disabled={formDisabled}
              showSearch={true}
            />
          </div>

          <div>
            <label style={styles.label}>
              <MessageSquare size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-1px" }} />
              Review Comments
              {documentTypeChanged && <span style={styles.required}>*</span>}
            </label>

            <textarea
              style={{
                ...styles.textarea,
                opacity: formDisabled ? 0.7 : 1,
                background: saving ? "#f8fafc" : "#ffffff",
                cursor: saving ? "not-allowed" : "text",
                border:
                  documentTypeChanged && !form.review_comments?.trim()
                    ? "1px solid #dc2626"
                    : "1px solid #cbd5e1",
              }}
              disabled={formDisabled}
              placeholder={
                documentTypeChanged
                  ? "Document type changed. Comments required."
                  : "Optional review comments"
              }
              value={form.review_comments}
              onChange={(e) => updateField("review_comments", e.target.value)}
            />
          </div>

          {/* Close Request Checkbox */}
          <div
            style={{
              marginTop: "16px",
              padding: "11px 13px",
              borderRadius: "8px",
              background: closeRequest ? "#eff6ff" : "#f8fafc",
              border: closeRequest ? "1px solid #93c5fd" : "1px solid #e2e8f0",
              transition: "all 0.15s ease",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                cursor: formDisabled ? "not-allowed" : "pointer",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={closeRequest}
                onChange={(e) => {
                  if (e.target.checked) {
                    setShowCloseConfirmModal(true);
                  } else {
                    setCloseRequest(false);
                  }
                }}
                disabled={formDisabled}
                style={{
                  width: "16px",
                  height: "16px",
                  marginTop: "2px",
                  cursor: formDisabled ? "not-allowed" : "pointer",
                  accentColor: "#2563eb",
                }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: closeRequest ? "#1d4ed8" : "#1e293b",
                  }}
                >
                  Close request
                </span>
                <span style={{ fontSize: "11.5px", color: "#64748b", lineHeight: 1.4 }}>
                  Closes &amp; resolves this case in the Work Pool.
                </span>
              </div>
            </label>
          </div>
        </div>

        {/* =========================================
          ATTACHMENTS
      ========================================= */}

        {/* <div style={styles.attachmentSection}>
          <div style={styles.sectionTitle}>Attachments</div>

          {row.attachments?.map((a, idx) => (
            <div key={idx} style={styles.attachmentCard}>
              <div style={styles.attachmentLeft}>
                <FileText size={15} />

                <div>
                  <div style={styles.attachmentName}>{a.name}</div>
                </div>
              </div>

              <button
                style={styles.previewBtn}
                onClick={() => onPreview(a.path)}
                disabled={previewLoading}
              >
                <Eye size={14} />
              </button>
            </div>
          ))}
        </div> */}
      </div>
      {/* =========================================
          FOOTER
      ========================================= */}

      <div style={styles.footer}>
        <button
          style={{
            ...styles.cancelBtn,
            opacity: saving ? 0.6 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
          onClick={onClose}
          disabled={formDisabled}
        >
          Cancel
        </button>

        {onReindex && (
          <button
            type="button"
            style={{
              ...styles.cancelBtn,
              flex: 0.8,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
            }}
            onClick={() => onReindex(row.msg_id)}
            disabled={formDisabled}
            title="Re-run indexing parser"
          >
            <RefreshCw size={13} />
            ReIndex
          </button>
        )}

        <button
          style={{
            ...styles.saveBtn,
            background: closeRequest ? "#16a34a" : styles.saveBtn.background,
            opacity: formDisabled ? 0.7 : 1,
            cursor: formDisabled ? "not-allowed" : "pointer",
          }}
          onClick={update}
          disabled={formDisabled}
        >
          <Save size={15} />
          {saving
            ? (closeRequest ? "Saving & Closing..." : "Updating...")
            : updated
              ? (closeRequest ? "Saved & Closed" : "Updated")
              : (closeRequest ? "Save & Close" : "Update")}
        </button>
      </div>

      {/* =========================================
          CLOSE REQUEST CONFIRMATION MODAL
      ========================================= */}
      {showCloseConfirmModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
            animation: "fadeIn 0.15s ease-out",
          }}
          onClick={() => setShowCloseConfirmModal(false)}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.35)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "20px 20px 14px 20px", display: "flex", gap: "14px", alignItems: "flex-start" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  background: "#fee2e2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AlertCircle size={22} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ margin: "0 0 6px 0", fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                  Confirm Close Request
                </h4>
                <p style={{ margin: 0, fontSize: "13px", color: "#475569", lineHeight: 1.5 }}>
                  Are you sure you want to close this request? This case will be <strong>closed &amp; resolved</strong> in the Work Pool and on <strong>Azure DevOps</strong>.
                </p>
                <div style={{ marginTop: "10px", padding: "8px 10px", background: "#f8fafc", borderRadius: "6px", fontSize: "12px", color: "#64748b" }}>
                  Policy: <strong style={{ color: "#0f172a" }}>{form.policy_number || row.policy_number || "—"}</strong>
                  {(row.ado_user_story_id || row.id) && (
                    <span> · User Story: <strong style={{ color: "#0f172a" }}>#{row.ado_user_story_id || row.id}</strong></span>
                  )}
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                padding: "14px 20px",
                background: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <button
                type="button"
                onClick={() => setShowCloseConfirmModal(false)}
                style={{
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontSize: "12.5px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setCloseRequest(true);
                  setShowCloseConfirmModal(false);
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#2563eb",
                  color: "#ffffff",
                  fontSize: "12.5px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <CheckCircle2 size={14} />
                Confirm &amp; Mark Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================
          ERROR MODAL POPUP DIALOG
      ========================================= */}
      {errorModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={handleDismissErrorModal}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "460px",
              boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.35)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              animation: "fadeIn 0.15s ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "24px 24px 18px 24px", display: "flex", alignItems: "flex-start", gap: "16px" }}>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "12px",
                  background: "#fee2e2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AlertCircle size={24} />
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: "0 0 6px 0", fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                  Verification &amp; Reassignment Alert
                </h4>
                <p style={{ margin: 0, fontSize: "13.5px", color: "#475569", lineHeight: "1.5" }}>
                  {errorModal}
                </p>
              </div>
            </div>

            <div
              style={{
                padding: "14px 20px",
                background: "#f8fafc",
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleDismissErrorModal}
                style={{
                  padding: "9px 28px",
                  borderRadius: "8px",
                  background: "#2563eb",
                  color: "#ffffff",
                  fontSize: "13px",
                  fontWeight: "700",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#1d4ed8")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#2563eb")}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================
   FIELD
========================================= */

function Field({ label, icon: Icon, value, onChange, disabled }) {
  return (
    <div>
      <label style={styles.label}>
        {Icon && <Icon size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-1px" }} />}
        {label}
      </label>

      <input
        style={{
          ...styles.input,
          opacity: disabled ? 0.7 : 1,
          background: disabled ? "#f8fafc" : "#ffffff",
          cursor: disabled ? "not-allowed" : "text",
        }}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

/* =========================================
   SEARCHABLE SELECT COMPONENT
========================================= */

function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  disabled = false,
  loading = false,
  showSearch = false,
  allowClear = false,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 200, openUpward: false });
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsRefs = useRef([]);

  const selected = options.find((o) => String(o.value) === String(value));

  const updatePosition = useCallback(() => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 250 && rect.top > spaceBelow;
      const width = Math.max(160, rect.width);

      let left = rect.left;
      if (left + width > window.innerWidth - 10) {
        left = window.innerWidth - width - 10;
      }
      if (left < 10) left = 10;

      let top = openUpward ? rect.top - 4 : rect.bottom + 4;
      setCoords({ top, left, width, openUpward });
    }
  }, []);

  const handleOpen = () => {
    if (disabled || loading) return;
    updatePosition();
    setOpen(true);
    setSearch("");
    setFocusedIndex(-1);
  };

  useEffect(() => {
    if (!open) return;
    const handleScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inWrap && !inDropdown) {
        setOpen(false);
        setSearch("");
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleMouseDown);
    }
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  useEffect(() => {
    if (open && showSearch) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 30);
      return () => clearTimeout(timer);
    }
  }, [open, showSearch]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(q) ||
        String(o.value || "").toLowerCase().includes(q) ||
        (o.sub || "").toLowerCase().includes(q)
    );
  }, [options, search]);

  useEffect(() => {
    if (open) {
      const idx = filtered.findIndex((o) => String(o.value) === String(value));
      setFocusedIndex(idx >= 0 ? idx : 0);
    }
  }, [open, filtered, value]);

  useEffect(() => {
    if (focusedIndex >= 0 && optionsRefs.current[focusedIndex]) {
      optionsRefs.current[focusedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch("");
  };

  const handleKeyDown = (e) => {
    if (disabled || loading) return;

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleOpen();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev + 1) % filtered.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
        }
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filtered.length) {
          handleSelect(filtered[focusedIndex].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setSearch("");
        break;
      case "Tab":
        setOpen(false);
        setSearch("");
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={wrapRef}
      id={id}
      style={{ position: "relative", width: "100%" }}
      onKeyDown={handleKeyDown}
    >
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (!open) handleOpen();
          else setOpen(false);
        }}
        style={{
          width: "100%",
          height: "38px",
          border: open ? "1px solid #2563eb" : "1px solid #cbd5e1",
          boxShadow: open ? "0 0 0 3px rgba(37, 99, 235, 0.08)" : "none",
          borderRadius: "8px",
          padding: "0 12px",
          fontSize: "13px",
          fontWeight: selected ? "600" : "500",
          color: selected ? "#0f172a" : "#94a3b8",
          outline: "none",
          boxSizing: "border-box",
          background: disabled ? "#f8fafc" : "#ffffff",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          transition: "all 0.15s ease",
          opacity: disabled ? 0.7 : 1,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            color: selected ? "#0f172a" : "#94a3b8",
          }}
        >
          {loading ? "Loading..." : selected ? selected.label : placeholder}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          {allowClear && !disabled && !loading && value ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
                setOpen(false);
              }}
              style={{
                color: "#94a3b8",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: "2px",
                borderRadius: "4px",
              }}
              title="Clear selection"
            >
              <X size={13} />
            </span>
          ) : null}

          <ChevronDown
            size={15}
            color="#64748b"
            style={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </div>
      </div>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              transform: coords.openUpward ? "translateY(-100%)" : "none",
              width: `${coords.width}px`,
              maxHeight: "260px",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "10px",
              boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.16), 0 8px 10px -6px rgba(15, 23, 42, 0.08)",
              zIndex: 999999,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            {showSearch && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 10px",
                  borderBottom: "1px solid #f1f5f9",
                  background: "#f8fafc",
                }}
              >
                <Search size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: "12.5px",
                    color: "#0f172a",
                    padding: 0,
                  }}
                  onKeyDown={handleKeyDown}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: "0 2px",
                      cursor: "pointer",
                      color: "#94a3b8",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                padding: "4px",
              }}
            >
              {filtered.length === 0 ? (
                <div style={{ padding: "12px", textAlign: "center", fontSize: "12px", color: "#94a3b8" }}>
                  No options found
                </div>
              ) : (
                filtered.map((opt, idx) => {
                  const isSel = String(opt.value) === String(value);
                  const isFoc = idx === focusedIndex;
                  return (
                    <div
                      key={opt.value || idx}
                      ref={(el) => (optionsRefs.current[idx] = el)}
                      onClick={() => handleSelect(opt.value)}
                      onMouseEnter={() => setFocusedIndex(idx)}
                      style={{
                        padding: "7px 10px",
                        borderRadius: "6px",
                        background: isSel
                          ? "rgba(37, 99, 235, 0.08)"
                          : isFoc
                          ? "#f1f5f9"
                          : "transparent",
                        color: isSel ? "#2563eb" : "#0f172a",
                        fontSize: "12.5px",
                        fontWeight: isSel ? "600" : "500",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "background 0.1s ease",
                      }}
                    >
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8 }}>
                        <span>{opt.label}</span>
                        {opt.sub && (
                          <div style={{ fontSize: "10.5px", color: "#64748b" }}>{opt.sub}</div>
                        )}
                      </div>
                      {isSel && <Check size={14} color="#2563eb" style={{ flexShrink: 0 }} />}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* =========================================
   STYLES
========================================= */

const styles = {
  textarea: {
    width: "100%",
    minHeight: "72px",
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "13px",
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
    fontFamily: "inherit",
    border: "1px solid #cbd5e1",
    transition: "all 0.15s ease",
  },

  required: {
    color: "#dc2626",
    marginLeft: "4px",
  },
  container: {
    width: "100%",
    height: "100%",
    minHeight: 0,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: "16px",
  },
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },

  header: {
    padding: "14px 18px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
    background: "#fafafa",
    borderTopLeftRadius: "16px",
    borderTopRightRadius: "16px",
  },

  title: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    marginTop: "2px",
    fontSize: "12px",
    color: "#64748b",
  },

  closeBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#64748b",
    padding: "4px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emailCard: {
    margin: "20px",
    padding: "16px",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    background: "#f8fafc",
  },

  emailRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  emailName: {
    fontWeight: "600",
    color: "#0f172a",
  },

  emailAddress: {
    fontSize: "13px",
    color: "#64748b",
  },

  subject: {
    marginTop: "14px",
    color: "#0f172a",
    fontWeight: "600",
  },

  attachments: {
    marginTop: "12px",
    color: "#2563eb",
    fontWeight: "600",
    fontSize: "13px",
  },

  formSection: {
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    overflowY: "auto",
  },

  label: {
    display: "block",
    marginBottom: "5px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  input: {
    width: "100%",
    height: "38px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "0 12px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#1e293b",
    outline: "none",
    boxSizing: "border-box",
    background: "#ffffff",
    transition: "all 0.15s ease",
  },

  error: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "500",
  },

  attachmentSection: {
    marginTop: "20px",
    padding: "0 20px 20px",
    maxHeight: "320px",
    overflowY: "auto",
  },

  sectionTitle: {
    fontWeight: "700",
    marginBottom: "14px",
    color: "#0f172a",
  },

  attachmentCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "10px",
  },

  attachmentLeft: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    minWidth: 0,
  },

  attachmentName: {
    fontWeight: "600",
    color: "#0f172a",
  },

  attachmentPath: {
    fontSize: "11px",
    color: "#64748b",
    wordBreak: "break-all",
  },

  previewBtn: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    border: "none",
    background: "#eff6ff",
    color: "#2563eb",
    cursor: "pointer",
  },

  footer: {
    padding: "14px 18px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    gap: "10px",
    background: "#ffffff",
    position: "sticky",
    bottom: 0,
    zIndex: 5,
    borderBottomLeftRadius: "16px",
    borderBottomRightRadius: "16px",
  },

  cancelBtn: {
    flex: 1,
    height: "40px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    transition: "all 0.15s ease",
  },

  saveBtn: {
    flex: 1.3,
    height: "40px",
    borderRadius: "8px",
    border: "none",
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
    transition: "all 0.15s ease",
  },

  reindexBtn: {
    flex: 1,
    height: "40px",
    borderRadius: "8px",
    border: "none",
    background: "#0f172a",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },

  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#64748b",
  },
};
